import { useState, useEffect } from 'react';
import { Send, Plus, Trash2, AlertCircle, CheckCircle2, Building } from 'lucide-react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/hooks/useI18n';
import { supabase } from '@/integrations/supabase/client';
import { formatCurrency, CATEGORY_LABELS } from '@/lib/index';
import { useToast } from '@/hooks/use-toast';

interface TransferForm {
  recipient_name: string;
  account_number: string;
  sort_code: string;
  amount: string;
  reference: string;
  category: string;
}

interface Beneficiary {
  id: string;
  user_id: string;
  name: string;
  account_number: string;
  sort_code?: string;
  currency: string;
  created_at: string;
}

interface RecentTransfer {
  id: string;
  amount: number;
  description: string;
  created_at: string;
  counterparty_name?: string;
  status: string;
}

export default function DashboardTransfer() {
  const { user, account: bankAccount, profile, refreshProfile } = useAuth();
  const { t } = useI18n();
  const { toast } = useToast();
  const [form, setForm] = useState<TransferForm>({
    recipient_name: '',
    account_number: '',
    sort_code: '',
    amount: '',
    reference: '',
    category: 'transfer',
  });
  const [beneficiaries, setBeneficiaries] = useState<Beneficiary[]>([]);
  const [recentTransfers, setRecentTransfers] = useState<RecentTransfer[]>([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [saveBeneficiary, setSaveBeneficiary] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const set = (field: keyof TransferForm, value: string) => setForm((p) => ({ ...p, [field]: value }));

  const CATEGORIES = [
    { value: 'transfer', label: `↔️ ${CATEGORY_LABELS.transfer}` },
    { value: 'food', label: `🍔 ${CATEGORY_LABELS.food}` },
    { value: 'shopping', label: `🛍️ ${CATEGORY_LABELS.shopping}` },
    { value: 'utilities', label: `💡 ${CATEGORY_LABELS.utilities}` },
    { value: 'transport', label: `🚗 ${CATEGORY_LABELS.transport}` },
    { value: 'health', label: `🏥 ${CATEGORY_LABELS.health}` },
    { value: 'entertainment', label: `🎬 ${CATEGORY_LABELS.entertainment}` },
    { value: 'other', label: `📦 ${CATEGORY_LABELS.other}` },
  ];

  const PLAN_LIMITS: Record<string, number> = {
    Standard: 1000,
    Plus: 5000,
    Premium: 25000,
    Metal: 100000,
  };

  const transferLimit = profile ? PLAN_LIMITS[profile.plan] || 1000 : 1000;

  useEffect(() => {
    if (!user) return;
    supabase
      .from('beneficiaries')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .then(({ data }) => setBeneficiaries((data ?? []) as Beneficiary[]));

    if (bankAccount) {
      supabase
        .from('transactions')
        .select('id, amount, description, created_at, counterparty_name, status')
        .eq('account_id', bankAccount.id)
        .eq('type', 'debit')
        .order('created_at', { ascending: false })
        .limit(5)
        .then(({ data }) => setRecentTransfers((data ?? []) as RecentTransfer[]));
    }
  }, [user, bankAccount]);

  const selectBeneficiary = (b: Beneficiary) => {
    setForm((p) => ({
      ...p,
      recipient_name: b.name,
      account_number: b.account_number,
      sort_code: b.sort_code || '',
    }));
  };

  const deleteBeneficiary = async (id: string) => {
    await supabase.from('beneficiaries').delete().eq('id', id);
    setBeneficiaries((prev) => prev.filter((b) => b.id !== id));
    toast({ title: 'Beneficjent usunięty', variant: 'default' });
  };

  const validateSortCode = (code: string): boolean => {
    const pattern = /^\d{2}-\d{2}-\d{2}$/;
    return pattern.test(code);
  };

  const validateAccountNumber = (num: string): boolean => {
    return /^\d{8}$/.test(num);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!user || !bankAccount) {
      setError('Błąd uwierzytelniania. Zaloguj się ponownie.');
      return;
    }

    if (!form.recipient_name.trim()) {
      setError('Wprowadź nazwę odbiorcy');
      return;
    }

    if (!validateAccountNumber(form.account_number)) {
      setError('Numer konta musi mieć 8 cyfr');
      return;
    }

    if (form.sort_code && !validateSortCode(form.sort_code)) {
      setError('Kod sortowania musi być w formacie XX-XX-XX');
      return;
    }

    const amount = parseFloat(form.amount);
    if (isNaN(amount) || amount <= 0) {
      setError('Wprowadź prawidłową kwotę');
      return;
    }
    if (amount > bankAccount.balance) {
      setError('Niewystarczające środki na koncie');
      return;
    }
    if (amount > transferLimit) {
      setError(`Limit przelewu dla planu ${profile?.plan}: ${formatCurrency(transferLimit)}`);
      return;
    }

    setShowConfirmation(true);
  };

  const executeTransfer = async () => {
    if (!user || !bankAccount) return;

    setLoading(true);
    setShowConfirmation(false);

    try {
      const amount = parseFloat(form.amount);
      const newBalance = bankAccount.balance - amount;
      const reference = form.reference || `REF${Date.now()}`;

      const { error: txError } = await supabase.from('transactions').insert({
        account_id: bankAccount.id,
        user_id: user.id,
        type: 'debit',
        amount: amount,
        currency: 'GBP',
        balance_after: newBalance,
        description: form.reference || `Przelew do ${form.recipient_name}`,
        reference: reference,
        category: form.category,
        counterparty_name: form.recipient_name,
        counterparty_account: form.account_number,
        counterparty_sort_code: form.sort_code || null,
        status: 'completed',
      });

      if (txError) throw txError;

      const { error: balanceError } = await supabase
        .from('bank_accounts')
        .update({ balance: newBalance, updated_at: new Date().toISOString() })
        .eq('id', bankAccount.id);

      if (balanceError) throw balanceError;

      if (saveBeneficiary && form.recipient_name && form.account_number) {
        const existingBen = beneficiaries.find(
          (b) => b.account_number === form.account_number && b.sort_code === form.sort_code
        );
        if (!existingBen) {
          const { data: newBen } = await supabase
            .from('beneficiaries')
            .insert({
              user_id: user.id,
              name: form.recipient_name,
              account_number: form.account_number,
              sort_code: form.sort_code || null,
              currency: 'GBP',
            })
            .select()
            .single();
          if (newBen) {
            setBeneficiaries((prev) => [newBen as Beneficiary, ...prev]);
          }
        }
      }

      setSuccess(true);
      setForm({
        recipient_name: '',
        account_number: '',
        sort_code: '',
        amount: '',
        reference: '',
        category: 'transfer',
      });
      setSaveBeneficiary(false);
      setTimeout(() => setSuccess(false), 5000);
      toast({
        title: 'Przelew zrealizowany',
        description: `${formatCurrency(amount)} → ${form.recipient_name}`,
      });
      await refreshProfile();

      if (bankAccount) {
        const { data } = await supabase
          .from('transactions')
          .select('id, amount, description, created_at, counterparty_name, status')
          .eq('account_id', bankAccount.id)
          .eq('type', 'debit')
          .order('created_at', { ascending: false })
          .limit(5);
        if (data) setRecentTransfers(data as RecentTransfer[]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Błąd podczas przelewu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout title="Przelew">
      <div className="max-w-6xl mx-auto grid gap-6 lg:grid-cols-[1fr_340px]">
        <div className="space-y-6">
          <Card className="border-border/50 bg-card shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Send className="w-5 h-5 text-accent" />
                Nowy przelew
              </CardTitle>
            </CardHeader>
            <CardContent>
              {success && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex items-center gap-3 bg-success-light border border-success/30 rounded-xl p-4 mb-6"
                >
                  <CheckCircle2 className="w-5 h-5 text-success shrink-0" />
                  <div>
                    <p className="text-sm text-success font-semibold">Przelew zrealizowany pomyślnie!</p>
                    <p className="text-xs text-success/80 mt-0.5">Środki zostały przetransferowane</p>
                  </div>
                </motion.div>
              )}
              {error && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex items-center gap-3 bg-danger-light border border-danger/30 rounded-xl p-4 mb-6"
                >
                  <AlertCircle className="w-5 h-5 text-danger shrink-0" />
                  <p className="text-sm text-danger font-medium">{error}</p>
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 gap-5">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Nazwa odbiorcy <span className="text-danger">*</span>
                    </Label>
                    <Input
                      placeholder="Jan Kowalski"
                      value={form.recipient_name}
                      onChange={(e) => set('recipient_name', e.target.value)}
                      className="h-12 bg-background border-border"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2 space-y-2">
                      <Label className="text-sm font-medium">
                        Numer konta <span className="text-danger">*</span>
                      </Label>
                      <Input
                        placeholder="12345678"
                        value={form.account_number}
                        onChange={(e) => set('account_number', e.target.value.replace(/\D/g, '').slice(0, 8))}
                        className="h-12 font-mono bg-background border-border"
                        maxLength={8}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Kod sortowania</Label>
                      <Input
                        placeholder="40-22-24"
                        value={form.sort_code}
                        onChange={(e) => {
                          let val = e.target.value.replace(/\D/g, '');
                          if (val.length > 2) val = val.slice(0, 2) + '-' + val.slice(2);
                          if (val.length > 5) val = val.slice(0, 5) + '-' + val.slice(5, 7);
                          set('sort_code', val);
                        }}
                        className="h-12 font-mono bg-background border-border"
                        maxLength={8}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Kwota (GBP) <span className="text-danger">*</span>
                    </Label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-bold text-lg">
                        £
                      </span>
                      <Input
                        type="number"
                        min="0.01"
                        step="0.01"
                        placeholder="0.00"
                        value={form.amount}
                        onChange={(e) => set('amount', e.target.value)}
                        className="h-14 pl-8 text-xl font-bold bg-background border-border"
                        required
                      />
                    </div>
                    {bankAccount && (
                      <div className="flex items-center justify-between text-xs">
                        <p className="text-muted-foreground">
                          Dostępne:{' '}
                          <span className="text-foreground font-medium">
                            {formatCurrency(bankAccount.balance)}
                          </span>
                        </p>
                        <p className="text-muted-foreground">
                          Limit: <span className="text-foreground font-medium">{formatCurrency(transferLimit)}</span>
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Opis przelewu</Label>
                    <Input
                      placeholder="Np. Opłata za usługi"
                      value={form.reference}
                      onChange={(e) => set('reference', e.target.value)}
                      className="h-12 bg-background border-border"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Kategoria</Label>
                    <Select value={form.category} onValueChange={(value) => set('category', value)}>
                      <SelectTrigger className="h-12 bg-background border-border">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-xl border border-border/50">
                    <input
                      type="checkbox"
                      id="save-ben"
                      checked={saveBeneficiary}
                      onChange={(e) => setSaveBeneficiary(e.target.checked)}
                      className="w-4 h-4 rounded accent-accent"
                    />
                    <label htmlFor="save-ben" className="text-sm text-foreground cursor-pointer">
                      Zapisz odbiorcę do listy beneficjentów
                    </label>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-13 bg-accent text-accent-foreground hover:bg-accent/90 font-semibold text-base py-4 shadow-lg transition-all"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-accent-foreground/30 border-t-accent-foreground rounded-full animate-spin" />
                      Przetwarzanie...
                    </span>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Wyślij przelew
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {recentTransfers.length > 0 && (
            <Card className="border-border/50 bg-card shadow-lg">
              <CardHeader>
                <CardTitle className="text-base">Ostatnie przelewy</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {recentTransfers.map((tx) => (
                    <div
                      key={tx.id}
                      className="flex items-center justify-between p-3 rounded-xl bg-muted/30 border border-border/50"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {tx.counterparty_name || tx.description}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(tx.created_at).toLocaleDateString('pl-PL', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                          })}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-danger">-{formatCurrency(tx.amount)}</p>
                        <Badge variant={tx.status === 'completed' ? 'default' : 'secondary'} className="text-xs">
                          {tx.status === 'completed' ? 'Zrealizowany' : 'W trakcie'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div>
          <Card className="border-border/50 bg-card shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Building className="w-4 h-4 text-accent" />
                Beneficjenci
              </CardTitle>
            </CardHeader>
            <CardContent>
              {beneficiaries.length === 0 ? (
                <div className="text-center py-8">
                  <Plus className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">Brak zapisanych beneficjentów</p>
                  <p className="text-xs text-muted-foreground mt-1">Zaznacz opcję podczas przelewu</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {beneficiaries.map((b) => (
                    <div
                      key={b.id}
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted/50 transition-colors cursor-pointer group border border-transparent hover:border-border/50"
                      onClick={() => selectBeneficiary(b)}
                    >
                      <div className="w-9 h-9 bg-accent/15 rounded-full flex items-center justify-center text-sm font-bold text-accent shrink-0">
                        {b.name[0]?.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{b.name}</p>
                        <p className="text-xs text-muted-foreground font-mono truncate">
                          {b.account_number}
                        </p>
                        {b.sort_code && (
                          <Badge variant="outline" className="text-xs mt-0.5">
                            {b.sort_code}
                          </Badge>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="w-7 h-7 opacity-0 group-hover:opacity-100 text-danger hover:text-danger/80 hover:bg-danger-light"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteBeneficiary(b.id);
                        }}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {showConfirmation && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border rounded-2xl shadow-2xl max-w-md w-full p-6"
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-accent/15 rounded-full flex items-center justify-center mx-auto mb-4">
                <Send className="w-8 h-8 text-accent" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">Potwierdź przelew</h3>
              <p className="text-sm text-muted-foreground">Sprawdź szczegóły przed wysłaniem</p>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex justify-between py-2 border-b border-border/50">
                <span className="text-sm text-muted-foreground">Odbiorca</span>
                <span className="text-sm font-medium text-foreground">{form.recipient_name}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-border/50">
                <span className="text-sm text-muted-foreground">Numer konta</span>
                <span className="text-sm font-mono font-medium text-foreground">{form.account_number}</span>
              </div>
              {form.sort_code && (
                <div className="flex justify-between py-2 border-b border-border/50">
                  <span className="text-sm text-muted-foreground">Kod sortowania</span>
                  <span className="text-sm font-mono font-medium text-foreground">{form.sort_code}</span>
                </div>
              )}
              <div className="flex justify-between py-2 border-b border-border/50">
                <span className="text-sm text-muted-foreground">Kwota</span>
                <span className="text-lg font-bold text-accent">{formatCurrency(parseFloat(form.amount))}</span>
              </div>
              {form.reference && (
                <div className="flex justify-between py-2">
                  <span className="text-sm text-muted-foreground">Opis</span>
                  <span className="text-sm font-medium text-foreground text-right">{form.reference}</span>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 h-12"
                onClick={() => setShowConfirmation(false)}
                disabled={loading}
              >
                Anuluj
              </Button>
              <Button
                className="flex-1 h-12 bg-accent text-accent-foreground hover:bg-accent/90 font-semibold"
                onClick={executeTransfer}
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-accent-foreground/30 border-t-accent-foreground rounded-full animate-spin" />
                    Wysyłanie...
                  </span>
                ) : (
                  'Potwierdź'
                )}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </DashboardLayout>
  );
}